export const SCHEMA_VERSION = "2026-08-06.2" as const;

export const modes = ["public", "private"] as const;
export const verticals = ["film", "tv", "advertising", "business", "general"] as const;
export const services = ["writing", "design", "writing_design"] as const;

export type Mode = (typeof modes)[number];
export type Vertical = (typeof verticals)[number];
export type Service = (typeof services)[number];
export type AnswerValue = string | string[] | boolean;
export type Answers = Record<string, AnswerValue>;

export type Rule =
  | { field: string; equals: string | boolean }
  | { field: string; includes: string }
  | { all: Rule[] }
  | { any: Rule[] }
  | { not: Rule };

export type AnswerType = "short" | "long" | "choice" | "multi" | "url" | "date";
export type Sensitivity = "public_safe" | "personal" | "confidential";

export type Guidance = {
  unit: "characters" | "words";
  recommendedMin?: number;
  recommendedMax?: number;
  hardMaxCharacters: number;
  note: string;
};

export type QuestionOption = {
  value: string;
  label: string;
  detail?: string;
};

export type BriefSection =
  | "project"
  | "decision"
  | "audience"
  | "story"
  | "proof"
  | "visual"
  | "production"
  | "risk"
  | "approval";

export type Question = {
  id: string;
  schemaVersion: typeof SCHEMA_VERSION;
  chapter: string;
  chapterTitle: string;
  chapterNote: string;
  label: string;
  shortLabel: string;
  whyAsked?: string;
  answerType: AnswerType;
  options?: QuestionOption[];
  placeholder?: string;
  required: boolean;
  verticals?: Vertical[];
  services?: Service[];
  modes?: Mode[];
  visibleWhen?: Rule;
  sensitivity: Sensitivity;
  uncertaintyAllowed: boolean;
  guidance?: Guidance;
  exampleId?: string;
  mapsTo: BriefSection[];
  provenance: string[];
};

export type DiscoveryContext = {
  mode: Mode;
  vertical: Vertical;
  service: Service;
};

export type Contact = {
  name: string;
  email: string;
  phone?: string;
  company?: string;
};

export type Draft = DiscoveryContext & {
  draftId: string;
  schemaVersion: typeof SCHEMA_VERSION;
  answers: Answers;
  contact: Contact;
  startedAt: string;
  updatedAt: string;
  currentChapter: number;
  resumeToken?: string;
};

export type Receipt = {
  submissionId: string;
  receiptId: string;
  revision: number;
  schemaVersion: string;
  payloadHash: string;
  status: "protected";
  receivedAt: string;
  notificationState: "queued" | "pending_configuration" | "pending_retry";
};

export type ReadinessState = "clear" | "needs_thought" | "unknown";

export type ReadinessItem = {
  id: "decision" | "story" | "proof" | "visual" | "ask_production";
  label: string;
  state: ReadinessState;
  reason: string;
};

export type GeneratedBrief = {
  title: string;
  routeLabel: string;
  generatedAt: string;
  projectBrief: Array<{ label: string; value: string }>;
  storyOutline: Array<{ title: string; purpose: string; sourceQuestionIds: string[] }>;
  assetChecklist: string[];
  evidenceIndex: Array<{ claim: string; source: string; state: ReadinessState }>;
  openQuestions: string[];
  risks: string[];
  doNotInvent: string[];
  nextActions: string[];
  readiness: ReadinessItem[];
};
